import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Chip,
  Grid,
  Divider,
  styled,
  Alert,
  AlertTitle,
  Button,
  useTheme
} from '@mui/material';
import ImageViewer from '../../../coreComponents/ImageViewer/index.tsx';
import { 
  Verified, 
  PendingActions, 
  ErrorOutline, 
  InsertDriveFile,
  Image as ImageIcon,
  CloudUpload
} from '@mui/icons-material';
import { STATUS } from '../type.ts';
import VerifyBox from './VerifyBox.tsx';
import LinkView from '../../../coreComponents/PDFViewer/LinkView.tsx';

interface DocumentProps {
  fileUrl: string;
  status: STATUS;
  documentNumber: string;
  documentName: string;
  onVerify: (status: STATUS, rejection_message: string) => Promise<boolean>;
  reason: string | null;
}

// Styled components
const DocumentContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  overflow: 'hidden',
}));

const DocumentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
}));

const DocumentTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const FilePreviewContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(3, 0),
  position: 'relative',
  transition: 'all 0.3s ease',
}));

const DocumentView = ({
  fileUrl = '',
  status,
  documentNumber,
  documentName,
  reason,
  onVerify,
}: DocumentProps) => {
  const theme = useTheme();
  const [verified, setVerified] = useState(status === STATUS.VERIFIED);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  
  // Check if the file is a PDF or image
  const isImage = fileUrl?.toLowerCase()?.match(/\.(jpeg|jpg|gif|png)$/i);
  const isPDF = fileUrl?.toLowerCase()?.endsWith('.pdf');

  const onVerification = async (status: STATUS, rejection_message: string) => {
    const verified = await onVerify(status, rejection_message);
    setVerified(verified);
  };

  // Function to get status details
  const getStatusDetails = () => {
    switch (status) {
      case STATUS.VERIFIED:
        return {
          icon: <Verified fontSize="small" />,
          label: 'Verified',
          color: 'success',
          bgColor: theme.palette.success.light,
          textColor: theme.palette.success.contrastText
        };
      case STATUS.REJECTED:
        return {
          icon: <ErrorOutline fontSize="small" />,
          label: 'Rejected',
          color: 'error',
          bgColor: theme.palette.error.light,
          textColor: theme.palette.error.contrastText
        };
      case STATUS.VERIFICATION_PENDING:
        return {
          icon: <PendingActions fontSize="small" />,
          label: 'Pending Verification',
          color: 'warning',
          bgColor: theme.palette.warning.light,
          textColor: theme.palette.warning.contrastText
        };
      case STATUS.UPLOADED:
        return {
          icon: <CloudUpload fontSize="small" />,
          label: 'Uploaded',
          color: 'info',
          bgColor: theme.palette.info.light,
          textColor: theme.palette.info.contrastText
        };
      case STATUS.PENDING_UPLOAD:
        return {
          icon: <ErrorOutline fontSize="small" />,
          label: 'Not Uploaded',
          color: 'info',
          bgColor: theme.palette.grey[300],
          textColor: theme.palette.text.primary
        };
      default:
        return {
          icon: <PendingActions fontSize="small" />,
          label: 'Unknown Status',
          color: 'info',
          bgColor: theme.palette.grey[300],
          textColor: theme.palette.text.primary
        };
    }
  };

  const statusDetails = getStatusDetails();

  return (
    <DocumentContainer elevation={0} variant="outlined">
      <DocumentHeader>
        <Box>
          <DocumentTitle variant="h6" gutterBottom>
            {isPDF ? <InsertDriveFile color="primary" /> : 
             isImage ? <ImageIcon color="primary" /> : 
             <InsertDriveFile color="primary" />}
            {documentName}
          </DocumentTitle>
          
          {documentNumber && (
            <Typography variant="body2" color="textSecondary">
              Document Number: <strong>{documentNumber}</strong>
            </Typography>
          )}
        </Box>
        
        <Chip
          icon={statusDetails.icon}
          label={statusDetails.label}
          sx={{
            marginLeft: theme.spacing(1),
            backgroundColor: statusDetails.bgColor,
            color: statusDetails.textColor
          }}
        />
      </DocumentHeader>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Verification Status Messages */}
      {verified ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>Document Verified</AlertTitle>
          This document has been verified and approved.
        </Alert>
      ) : status === STATUS.REJECTED && reason ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Document Rejected</AlertTitle>
          <Typography variant="body2">
            Reason: {reason}
          </Typography>
        </Alert>
      ) : status === STATUS.VERIFICATION_PENDING ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Verification In Progress</AlertTitle>
          This document has been submitted for verification and is waiting for approval.
        </Alert>
      ) : status === STATUS.UPLOADED ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Document Uploaded</AlertTitle>
          This document has been uploaded but verification has not been initiated yet.
        </Alert>
      ) : status === STATUS.PENDING_UPLOAD ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Document Not Uploaded</AlertTitle>
          This document has not been uploaded yet.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Unknown Status</AlertTitle>
          The status of this document is unknown.
        </Alert>
      )}
      
      {/* Verify Box - Only show for documents pending verification */}
      {status === STATUS.VERIFICATION_PENDING && (
        <Box sx={{ mb: 3 }}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              bgcolor: theme.palette.background.default 
            }}
          >
            <VerifyBox 
              onVerify={onVerification} 
              reason={reason}
              label={`Verify ${documentName}`}
            />
          </Paper>
        </Box>
      )}
      
      {/* Document Preview - Only show if there's a file URL */}
      {fileUrl && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Document Preview
            </Typography>
            
            <FilePreviewContainer
              sx={{
                height: previewExpanded ? 'auto' : '300px',
                overflow: previewExpanded ? 'visible' : 'hidden',
              }}
            >
              {isImage ? (
                <ImageViewer fileUrl={fileUrl} />
              ) : (
                <iframe
                  src={fileUrl}
                  title={documentName}
                  width="100%"
                  height="300"
                  style={{ border: 'none' }}
                />
              )}
              
              {/* {!previewExpanded && (
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    height: '80px', 
                    background: 'linear-gradient(rgba(255,255,255,0), rgba(255,255,255,1))',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    padding: 2
                  }}
                >
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setPreviewExpanded(true)}
                  >
                    Show More
                  </Button>
                </Box>
              )} */}
            </FilePreviewContainer>
            
            {previewExpanded && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setPreviewExpanded(false)}
                >
                  Show Less
                </Button>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" mt={2}>
              <LinkView link={fileUrl} />
            </Box>
          </Grid>
        </Grid>
      )}
    </DocumentContainer>
  );
};

export default DocumentView;